import { inject, makeEnvironmentProviders } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import {
  AppStatusRecord,
  CreateManagerRequest,
  ManagerRecord,
  UpdateManagerRequest,
  UpdateUserNicknameRequest,
  UserRecord,
  UserRole,
  WebColorScheme,
} from '@libs/shared/api';
import { provideEffects } from '@ngrx/effects';
import {
  createActionGroup,
  createFeature,
  createReducer,
  createSelector,
  emptyProps,
  on,
  props,
  provideState,
  Store,
} from '@ngrx/store';
import { AuthUserProfile } from '@sprint-backoffice/app/core/auth/auth-user-profile';
import { CoreEffects } from '@sprint-backoffice/app/core/state/core.effects';

export const coreFeatureKey = 'core';

export interface CoreState {
  appStatusState: {
    isLoading: boolean;
    isLoaded: boolean;
    content: AppStatusRecord;
    error: string;
  };
  userProfileState: {
    isLoading: boolean;
    isLoaded: boolean;
    content: AuthUserProfile;
    error: string;
  };
  currentUserRoleState: {
    isLoading: boolean;
    isLoaded: boolean;
    content: UserRole;
    error: string;
  };
  navigationItemsState: {
    isLoading: boolean;
    isLoaded: boolean;
    content: FuseNavigationItem[];
    error: string;
  };
  currentUserState: {
    isLoading: boolean;
    isLoaded: boolean;
    content: UserRecord;
    error: string;
  };
  currentManagerState: {
    isLoading: boolean;
    isLoaded: boolean;
    content: ManagerRecord;
    error: string;
  };
  populateAllDocumentsLockState: {
    isLoading: boolean;
    isLoaded: boolean;
    locked: boolean;
    error: string;
  };
}

export const initialState: CoreState = {
  appStatusState: {
    isLoading: false,
    isLoaded: false,
    content: undefined,
    error: '',
  },
  userProfileState: {
    isLoading: false,
    isLoaded: false,
    content: undefined,
    error: '',
  },
  currentUserRoleState: {
    isLoading: false,
    isLoaded: false,
    content: undefined,
    error: '',
  },
  navigationItemsState: {
    isLoading: false,
    isLoaded: false,
    content: [],
    error: '',
  },
  currentUserState: {
    isLoading: false,
    isLoaded: false,
    content: undefined,
    error: '',
  },
  currentManagerState: {
    isLoading: false,
    isLoaded: false,
    content: undefined,
    error: '',
  },
  populateAllDocumentsLockState: {
    isLoading: false,
    isLoaded: false,
    locked: false,
    error: '',
  },
};

// prettier-ignore
export const CorePageActions = createActionGroup(
	{
		source: 'Core Page',
		events: {
			'Logout Current User': emptyProps(),
			'Fetch App Status': emptyProps(),
			'Enable Member App': emptyProps(),
			'Disable Member App': emptyProps(),
			'Enable Backoffice App': emptyProps(),
			'Disable Backoffice App': emptyProps(),
			'Fetch Current User Role': emptyProps(),
			'Fetch Navigation Items': props<{ currentUserRole: UserRole }>(),
			'Fetch Auth User': emptyProps(),
			'Fetch or Create Current User': props<{ userProfile: AuthUserProfile }>(),
			'Fetch Current User By Id': props<{ userId: string }>(),
			'Create Current User': props<{ userProfile: AuthUserProfile }>(),
			'Update Current User Nickname': props<{ userId: string; request: UpdateUserNicknameRequest }>(),
			'Update Current User Profile Image': props<{ userId: string; imageFile: Blob; contentType: string }>(),
			'Delete Current User Profile Image': props<{ userId: string }>(),
			'Sync Current User Role': props<{ userId: string }>(),
			'Create Current Manager': props<{ request: CreateManagerRequest }>(),
			'Update Current Manager': props<{ userId: string; request: UpdateManagerRequest }>(),
			'Fetch Current Manager': props<{ userId: string }>(),
			'Complete Current Manager Education': emptyProps(),
			'Complete Current Manager Orientation': emptyProps(),
			'Complete Current Manager Test': emptyProps(),
			'Update Manager WebColorScheme': props<{ webColorScheme: WebColorScheme }>(),
      'Check Populate All Documents Lock': emptyProps(),
		},
	},
);

// prettier-ignore
export const CoreApiActions = createActionGroup({
	source: 'Core API',
	events: {
		'Fetch App Status Success': props<{ appStatus: AppStatusRecord }>(),
		'Fetch App Status Failure': props<{ error: string }>(),
		'Enable Member App Success': props<{ appStatus: AppStatusRecord }>(),
		'Enable Member App Failure': props<{ error: string }>(),
		'Disable Member App Success': props<{ appStatus: AppStatusRecord }>(),
		'Disable Member App Failure': props<{ error: string }>(),
		'Enable Backoffice App Success': props<{ appStatus: AppStatusRecord }>(),
		'Enable Backoffice App Failure': props<{ error: string }>(),
		'Disable Backoffice App Success': props<{ appStatus: AppStatusRecord }>(),
		'Disable Backoffice App Failure': props<{ error: string }>(),
		'Fetch Current User Role Success': props<{ currentUserRole: UserRole }>(),
		'Fetch Current User Role Failure': props<{ error: string }>(),
		'Fetch Navigation Items Success': props<{ navigationItems: FuseNavigationItem[]; }>(),
		'Fetch Navigation Items Failure': props<{ error: string }>(),
		'Fetch Auth User Success': props<{ userProfile: AuthUserProfile }>(),
		'Fetch Auth User Failure': props<{ error: string }>(),
		'Fetch or Create Current User Success': props<{ user: UserRecord }>(),
		'Fetch or Create Current User Failure': props<{ error: string }>(),
		'Fetch Current User by Id Success': props<{ user: UserRecord }>(),
		'Fetch Current User by Id Failure': props<{ error: string }>(),
		'Fetch Current User Not Found': emptyProps(),
		'Create Current User Success': emptyProps(),
		'Create Current User Failure': props<{ error: string }>(),
		'Sync Current User Role Success': emptyProps(),
		'Sync Current User Role Failure': props<{ error: string }>(),
		'Update Current User Nickname Success': emptyProps(),
		'Update Current User Nickname Failure': props<{ error: string }>(),
		'Update Current User Profile Image Success': emptyProps(),
		'Update Current User Profile Image Failure': props<{ error: string }>(),
		'Delete Current User Profile Image Success': emptyProps(),
		'Delete Current User Profile Image Failure': props<{ error: string }>(),
		'Create Current Manager Success': emptyProps(),
		'Create Current Manager Failure': props<{ error: string }>(),
		'Update Current Manager Success': emptyProps(),
		'Update Current Manager Failure': props<{ error: string }>(),
		'Fetch Current Manager Success': props<{ manager: ManagerRecord }>(),
		'Fetch Current Manager Not Found': emptyProps(),
		'Fetch Current Manager Failure': props<{ error: string }>(),
		'Complete Current Manager Education Success': emptyProps(),
		'Complete Current Manager Education Failure': props<{ error: string }>(),
		'Complete Current Manager Test Success': emptyProps(),
		'Complete Current Manager Test Failure': props<{ error: string }>(),
		'Complete Current Manager Orientation Success': emptyProps(),
		'Complete Current Manager Orientation Failure': props<{ error: string }>(),
		'Update Manager WebColorScheme Success': emptyProps(),
		'Update Manager WebColorScheme Failure': props<{ error: string }>(),
    'Check Populate All Documents Lock Success': props<{ locked: boolean }>(),
    'Check Populate All Documents Lock Failure': props<{ error: string }>(),
	},
});

export const coreFeature = createFeature({
  name: coreFeatureKey,
  reducer: createReducer(
    initialState,
    on(
      CorePageActions.fetchAppStatus,
      (state): CoreState => ({
        ...state,
        appStatusState: {
          ...state.appStatusState,
          isLoading: true,
        },
      })
    ),
    on(
      CoreApiActions.fetchAppStatusSuccess,
      (state, action): CoreState => ({
        ...state,
        appStatusState: {
          ...state.appStatusState,
          isLoading: false,
          isLoaded: true,
          content: action.appStatus,
          error: '',
        },
      })
    ),
    on(
      CoreApiActions.fetchAppStatusFailure,
      (state, action): CoreState => ({
        ...state,
        appStatusState: {
          ...state.appStatusState,
          isLoading: false,
          isLoaded: true,
          content: undefined,
          error: action.error,
        },
      })
    ),
    on(
      CorePageActions.enableMemberApp,
      (state): CoreState => ({
        ...state,
        appStatusState: {
          ...state.appStatusState,
          isLoading: true,
        },
      })
    ),
    on(
      CoreApiActions.enableMemberAppSuccess,
      (state, action): CoreState => ({
        ...state,
        appStatusState: {
          ...state.appStatusState,
          isLoading: false,
          content: action.appStatus,
          error: '',
        },
      })
    ),
    on(
      CoreApiActions.enableMemberAppFailure,
      (state, action): CoreState => ({
        ...state,
        appStatusState: {
          ...state.appStatusState,
          isLoading: false,
          content: undefined,
          error: action.error,
        },
      })
    ),
    on(
      CorePageActions.disableMemberApp,
      (state): CoreState => ({
        ...state,
        appStatusState: {
          ...state.appStatusState,
          isLoading: true,
        },
      })
    ),
    on(
      CoreApiActions.disableMemberAppSuccess,
      (state, action): CoreState => ({
        ...state,
        appStatusState: {
          ...state.appStatusState,
          isLoading: false,
          content: action.appStatus,
          error: '',
        },
      })
    ),
    on(
      CoreApiActions.disableMemberAppFailure,
      (state, action): CoreState => ({
        ...state,
        appStatusState: {
          ...state.appStatusState,
          isLoading: false,
          content: undefined,
          error: action.error,
        },
      })
    ),
    on(
      CorePageActions.enableBackofficeApp,
      (state): CoreState => ({
        ...state,
        appStatusState: {
          ...state.appStatusState,
          isLoading: true,
        },
      })
    ),
    on(
      CoreApiActions.enableBackofficeAppSuccess,
      (state, action): CoreState => ({
        ...state,
        appStatusState: {
          ...state.appStatusState,
          isLoading: false,
          content: action.appStatus,
          error: '',
        },
      })
    ),
    on(
      CoreApiActions.enableBackofficeAppFailure,
      (state, action): CoreState => ({
        ...state,
        appStatusState: {
          ...state.appStatusState,
          isLoading: false,
          content: undefined,
          error: action.error,
        },
      })
    ),
    on(
      CorePageActions.disableBackofficeApp,
      (state): CoreState => ({
        ...state,
        appStatusState: {
          ...state.appStatusState,
          isLoading: true,
        },
      })
    ),
    on(
      CoreApiActions.disableBackofficeAppSuccess,
      (state, action): CoreState => ({
        ...state,
        appStatusState: {
          ...state.appStatusState,
          isLoading: false,
          content: action.appStatus,
          error: '',
        },
      })
    ),
    on(
      CoreApiActions.disableBackofficeAppFailure,
      (state, action): CoreState => ({
        ...state,
        appStatusState: {
          ...state.appStatusState,
          isLoading: false,
          content: undefined,
          error: action.error,
        },
      })
    ),
    on(
      CorePageActions.fetchCurrentUserRole,
      (state): CoreState => ({
        ...state,
        currentUserRoleState: {
          ...state.currentUserRoleState,
          isLoading: true,
        },
      })
    ),
    on(
      CoreApiActions.fetchCurrentUserRoleSuccess,
      (state, action): CoreState => ({
        ...state,
        currentUserRoleState: {
          ...state.currentUserRoleState,
          isLoading: false,
          isLoaded: true,
          content: action.currentUserRole,
          error: '',
        },
      })
    ),
    on(
      CoreApiActions.fetchCurrentUserRoleFailure,
      (state, action): CoreState => ({
        ...state,
        currentUserRoleState: {
          ...state.currentUserRoleState,
          isLoading: false,
          isLoaded: true,
          content: undefined,
          error: action.error,
        },
      })
    ),
    on(
      CorePageActions.fetchNavigationItems,
      (state): CoreState => ({
        ...state,
        navigationItemsState: {
          ...state.navigationItemsState,
          isLoading: true,
        },
      })
    ),
    on(
      CoreApiActions.fetchNavigationItemsSuccess,
      (state, action): CoreState => ({
        ...state,
        navigationItemsState: {
          ...state.navigationItemsState,
          isLoading: false,
          isLoaded: true,
          content: action.navigationItems,
          error: '',
        },
      })
    ),
    on(
      CoreApiActions.fetchNavigationItemsFailure,
      (state, action): CoreState => ({
        ...state,
        navigationItemsState: {
          ...state.navigationItemsState,
          isLoading: false,
          isLoaded: true,
          content: undefined,
          error: action.error,
        },
      })
    ),
    on(
      CorePageActions.fetchAuthUser,
      (state): CoreState => ({
        ...state,
        userProfileState: {
          ...state.userProfileState,
          isLoading: true,
        },
      })
    ),
    on(
      CoreApiActions.fetchAuthUserSuccess,
      (state, action): CoreState => ({
        ...state,
        userProfileState: {
          ...state.userProfileState,
          isLoading: false,
          isLoaded: true,
          content: action.userProfile,
          error: '',
        },
      })
    ),
    on(
      CoreApiActions.fetchAuthUserFailure,
      (state, action): CoreState => ({
        ...state,
        userProfileState: {
          ...state.userProfileState,
          isLoading: false,
          isLoaded: true,
          content: undefined,
          error: action.error,
        },
      })
    ),
    on(
      CorePageActions.fetchOrCreateCurrentUser,
      (state): CoreState => ({
        ...state,
        currentUserState: {
          ...state.currentUserState,
          isLoading: true,
        },
      })
    ),
    on(
      CoreApiActions.fetchOrCreateCurrentUserSuccess,
      (state, action): CoreState => ({
        ...state,
        currentUserState: {
          ...state.currentUserState,
          isLoading: false,
          isLoaded: true,
          content: action.user,
          error: '',
        },
      })
    ),
    on(
      CoreApiActions.fetchOrCreateCurrentUserFailure,
      (state, action): CoreState => ({
        ...state,
        currentUserState: {
          ...state.currentUserState,
          isLoading: false,
          isLoaded: true,
          content: undefined,
          error: action.error,
        },
      })
    ),
    on(
      CorePageActions.fetchCurrentUserById,
      (state): CoreState => ({
        ...state,
        currentUserState: {
          ...state.currentUserState,
          isLoading: true,
        },
      })
    ),
    on(
      CoreApiActions.fetchCurrentUserByIdSuccess,
      (state, action): CoreState => ({
        ...state,
        currentUserState: {
          ...state.currentUserState,
          isLoading: false,
          isLoaded: true,
          content: action.user,
          error: '',
        },
      })
    ),
    on(
      CoreApiActions.fetchCurrentUserByIdFailure,
      (state, action): CoreState => ({
        ...state,
        currentUserState: {
          ...state.currentUserState,
          isLoading: false,
          isLoaded: true,
          content: undefined,
          error: action.error,
        },
      })
    ),
    on(
      CoreApiActions.createCurrentUserSuccess,
      (state): CoreState => ({
        ...state,
      })
    ),
    on(
      CoreApiActions.createCurrentUserFailure,
      (state, action): CoreState => ({
        ...state,
        currentUserState: {
          ...state.currentUserState,
          isLoading: false,
          isLoaded: true,
          error: action.error,
        },
      })
    ),
    on(
      CoreApiActions.updateCurrentUserProfileImageFailure,
      CoreApiActions.deleteCurrentUserProfileImageFailure,
      CoreApiActions.updateCurrentUserNicknameFailure,
      (state, action): CoreState => ({
        ...state,
        currentUserState: {
          ...state.currentUserState,
          error: action.error,
        },
      })
    ),
    on(
      CorePageActions.fetchCurrentManager,
      (state): CoreState => ({
        ...state,
        currentManagerState: {
          ...state.currentManagerState,
          isLoading: true,
        },
      })
    ),
    on(
      CoreApiActions.fetchCurrentManagerSuccess,
      (state, action): CoreState => ({
        ...state,
        currentManagerState: {
          ...state.currentManagerState,
          isLoading: false,
          isLoaded: true,
          content: action.manager,
          error: '',
        },
      })
    ),
    on(
      CoreApiActions.fetchCurrentManagerNotFound,
      (state): CoreState => ({
        ...state,
        currentManagerState: {
          ...state.currentManagerState,
          isLoading: false,
          isLoaded: true,
          content: undefined,
          error: '',
        },
      })
    ),
    on(
      CoreApiActions.fetchCurrentManagerFailure,
      (state, action): CoreState => ({
        ...state,
        currentManagerState: {
          ...state.currentManagerState,
          isLoading: false,
          isLoaded: true,
          content: undefined,
          error: action.error,
        },
      })
    ),
    on(
      CoreApiActions.checkPopulateAllDocumentsLockSuccess,
      (state, action): CoreState => ({
        ...state,
        populateAllDocumentsLockState: {
          ...state.populateAllDocumentsLockState,
          isLoading: false,
          isLoaded: true,
          locked: action.locked,
          error: '',
        },
      })
    ),
    on(
      CoreApiActions.checkPopulateAllDocumentsLockFailure,
      (state, action): CoreState => ({
        ...state,
        populateAllDocumentsLockState: {
          ...state.populateAllDocumentsLockState,
          isLoading: false,
          isLoaded: true,
          locked: false,
          error: action.error,
        },
      })
    )
  ),
  extraSelectors: ({
    selectAppStatusState,
    selectUserProfileState,
    selectCurrentUserState,
    selectCurrentManagerState,
    selectCurrentUserRoleState,
    selectNavigationItemsState,
  }) => ({
    selectAppStatus: createSelector(
      selectAppStatusState,
      (state): AppStatusRecord => state.content
    ),
    selectUserProfile: createSelector(
      selectUserProfileState,
      (state): AuthUserProfile => state.content
    ),
    selectCurrentUser: createSelector(
      selectCurrentUserState,
      (state): UserRecord => state.content
    ),
    selectCurrentUserId: createSelector(
      selectUserProfileState,
      (state): string => state.content.info.sub
    ),
    selectCurrentManager: createSelector(
      selectCurrentManagerState,
      (state): ManagerRecord => state.content
    ),
    selectCurrentUserRole: createSelector(
      selectCurrentUserRoleState,
      (state): UserRole => state.content
    ),
    selectNavigationItems: createSelector(
      selectNavigationItemsState,
      (state): FuseNavigationItem[] => state.content
    ),
    selectWebColorScheme: createSelector(
      selectCurrentManagerState,
      (state): WebColorScheme => state.content?.webColorScheme
    ),
  }),
});

export const {
  selectAppStatusState,
  selectUserProfileState,
  selectCurrentUserState,
  selectCurrentManagerState,
  selectCurrentUserRoleState,
  selectNavigationItemsState,
  selectPopulateAllDocumentsLockState,
  selectAppStatus,
  selectUserProfile,
  selectCurrentUser,
  selectCurrentUserId,
  selectCurrentManager,
  selectCurrentUserRole,
  selectNavigationItems,
  selectWebColorScheme,
} = coreFeature;

export function provideCoreFeature() {
  return makeEnvironmentProviders([
    provideState(coreFeature),
    provideEffects(CoreEffects),
  ]);
}

// prettier-ignore
export function injectCoreFeature() {
	const store = inject(Store);

	return {
		appStatusState$$: store.selectSignal(selectAppStatusState),
		userProfileState$$: store.selectSignal(selectUserProfileState),
		currentUserState$$: store.selectSignal(selectCurrentUserState),
		currentManagerState$$: store.selectSignal(selectCurrentManagerState),
		currentUserRoleState$$: store.selectSignal(selectCurrentUserRoleState),
		navigationItemsState$$: store.selectSignal(selectNavigationItemsState),
    populateAllDocumentsLockState$$: store.selectSignal(selectPopulateAllDocumentsLockState),
		appStatus$$: store.selectSignal(selectAppStatus),
		userProfile$$: store.selectSignal(selectUserProfile),
		currentUser$$: store.selectSignal(selectCurrentUser),
		currentUserId$$: store.selectSignal(selectCurrentUserId),
		currentManager$$: store.selectSignal(selectCurrentManager),
		currentUserRole$$: store.selectSignal(selectCurrentUserRole),
		navigationItems$$: store.selectSignal(selectNavigationItems),
		webColorScheme$$: store.selectSignal(selectWebColorScheme),

		fetchAppStatus: () => store.dispatch(CorePageActions.fetchAppStatus()),
		enableMemberApp: () => store.dispatch(CorePageActions.enableMemberApp()),
		disableMemberApp: () => store.dispatch(CorePageActions.disableMemberApp()),
		enableBackofficeApp: () => store.dispatch(CorePageActions.enableBackofficeApp()),
		disableBackofficeApp: () => store.dispatch(CorePageActions.disableBackofficeApp()),
		fetchCurrentUserRole: () => store.dispatch(CorePageActions.fetchCurrentUserRole()),
		fetchNavigationItems: (currentUserRole: UserRole) => store.dispatch(CorePageActions.fetchNavigationItems({ currentUserRole })),
		fetchAuthUser: () => store.dispatch(CorePageActions.fetchAuthUser()),
		fetchOrCreateCurrentUser: (userProfile: AuthUserProfile) => store.dispatch(CorePageActions.fetchOrCreateCurrentUser({ userProfile })),
		fetchCurrentUserById: (userId: string) => store.dispatch(CorePageActions.fetchCurrentUserById({ userId })),
		createCurrentUser: (userProfile: AuthUserProfile) => store.dispatch(CorePageActions.createCurrentUser({ userProfile })),
		updateCurrentUserNickname: (userId: string, request: UpdateUserNicknameRequest) => store.dispatch(CorePageActions.updateCurrentUserNickname({ userId, request })),
		updateCurrentUserProfileImage: (userId: string, imageFile: Blob, contentType: string) => store.dispatch(CorePageActions.updateCurrentUserProfileImage({ userId, imageFile, contentType })),
		deleteCurrentUserProfileImage: (userId: string) => store.dispatch(CorePageActions.deleteCurrentUserProfileImage({ userId })),
		syncCurrentUserRole: (userId: string) => store.dispatch(CorePageActions.syncCurrentUserRole({ userId })),
		createCurrentManager: (request: CreateManagerRequest) => store.dispatch(CorePageActions.createCurrentManager({ request })),
		updateCurrentManager: (userId: string, request: UpdateManagerRequest) => store.dispatch(CorePageActions.updateCurrentManager({ userId, request })),
		fetchCurrentManager: (userId: string) => store.dispatch(CorePageActions.fetchCurrentManager({ userId: userId })),
		completeCurrentManagerEducation: () => store.dispatch(CorePageActions.completeCurrentManagerEducation()),
		completeCurrentManagerOrientation: () => store.dispatch(CorePageActions.completeCurrentManagerOrientation()),
		completeCurrentManagerTest: () => store.dispatch(CorePageActions.completeCurrentManagerTest()),
		updateManagerWebColorScheme: (webColorScheme: WebColorScheme) => store.dispatch(CorePageActions.updateManagerWebColorScheme({ webColorScheme })),
    checkPopulateAllDocumentsLock: () => store.dispatch(CorePageActions.checkPopulateAllDocumentsLock()),
	};
}
